import { useCallback } from "react";

import { keepPreviousData, type UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import {
    type DigitalAssetsRecordsResponseDto,
    type DigitalAssetType,
    digitalAssetTypeMap,
    type WebsitesDigitalAssetRecord,
} from "../types/digital-assets";

import { companyAnalysisQueryKeys } from "./queryKeyFactory";

export const useDigitalAssetsQuery = <T extends DigitalAssetType = DigitalAssetType>(
    domain: string,
    assetType: T,
    withTotal = true,
): UseQueryResult<DigitalAssetsRecordsResponseDto<T>> => {
    const select = useCallback(
        (data: DigitalAssetsRecordsResponseDto<T>): DigitalAssetsRecordsResponseDto<T> => {
            if (!withTotal && assetType === digitalAssetTypeMap.websites) {
                return data.filter((item) => (item as WebsitesDigitalAssetRecord).domain);
            }

            return data;
        },
        [assetType, withTotal],
    );

    return useQuery({
        enabled: assetType === digitalAssetTypeMap.apps ? salesSettingsHelper.isConsolidatedAppsEnabled : true,
        staleTime: Number.POSITIVE_INFINITY,
        placeholderData: keepPreviousData,
        queryKey: companyAnalysisQueryKeys.digitalAssets(assetType, domain),
        queryFn: ({ signal, queryKey }) => {
            const [, , queryAssetType, queryDomain] = queryKey;

            return salesApiService.companyAnalysis.fetchDigitalAssets(queryDomain, queryAssetType, {
                cancellation: signal,
            });
        },
        select,
    });
};
